import { useEffect } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";

// ClientDeleteConfirmModal — feature-local T-4 destructive workflow
// confirmation widget.
//
// Closed-class reference: architecture-freeze-r8.md §4 T-4
// ("parent-header trigger + feature-local confirmation modal +
// DELETE + navigate('/clients', { replace: true, state:
// { mayBeStale: true } })").
//
// Topology class: D2 workflow transition (M5) / T-4 destructive
// workflow (R8 §4). NOT a styling variant of the T-1 edit modal —
// the successor state is structurally different: the resource
// ceases to exist; the current surface ceases to be reachable;
// navigation IS the consistency boundary.
//
// I6 §"Q4 procedural test" compliance — this widget is local iff:
//   1. Signature names no entity, endpoint, resource, or workflow.
//      (Props: open / clientName / deleting / errorMessage /
//      onCancel / onConfirm — copy is the parent's responsibility
//      via clientName; no entityType / endpoint / resourceKey.)
//   2. Body inlines the confirmation copy and cascade warning
//      (no config schema, no items prop, no template strings).
//   3. Consumed by exactly ONE feature (clientDetail).
//   4. Deleting this file affects exactly ONE feature.
//   5. Passes none of D-T1/D-T2/D-T5/D-T6 — no entity prop, no
//      shared consumers, no cascade pre-flight, no undo affordance.
//
// I6-proposed §6.5 (A-2) compliance:
//   1. Feature-local widget; signature names no entity/endpoint.
//   2. Success path is navigation (parent owns it) — not navigation
//      + section refresh.
//   3. Cross-screen freshness via mayBeStale only (parent emits).
//   4. No optimistic collection patching anywhere.
//   5. No cascade pre-flight — warning copy is static text inlined
//      below, mirroring design/clientDetail.html cd-delete-modal.
//   6. No event semantics (no WS, no BroadcastChannel, no storage).
//   7. No undo affordance.
//   8. No pre-emptive permission eviction; backend's 204 / 403 /
//      404 IS the authority. Owner gating is the parent's
//      client.is_owner check on render.
//
// Allowed UI-local effects per CLAUDE.md "Presentation Primitive
// Rules": ESC-key handler + body scroll lock. No selectors, no
// dispatch, no imperative handle, no registry, no portal, no
// shared modal host. Reuses the feature-local modal CSS vocabulary
// established by S4's ClientEditModal — the modal shell is the
// same; the destructive language is expressed locally via the
// __action-btn--danger modifier and the inlined warning copy.

function ClientDeleteConfirmModal({
  open,
  clientName,
  deleting,
  errorMessage,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, deleting, onCancel]);

  if (!open) return null;

  const safeName = (clientName || "").trim() || "цього клієнта";

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-delete-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={deleting ? undefined : onCancel}
      />
      <div className="feature-client-detail__modal-dialog feature-client-detail__modal-dialog--sm">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-delete-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i
              className="fa-solid fa-triangle-exclamation"
              aria-hidden="true"
            />
            <span>Видалити клієнта</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onCancel}
            disabled={deleting}
            aria-label="Закрити вікно підтвердження"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <p className="feature-client-detail__delete-question">
            Ви впевнені, що хочете видалити <strong>{safeName}</strong>?
          </p>
          <p className="feature-client-detail__delete-cascade">
            <i
              className="fa-solid fa-triangle-exclamation"
              aria-hidden="true"
            />
            <span>
              Разом з клієнтом видаляться його уподобання, матчі, перегляди,
              журнал взаємодій, задачі і шери.
            </span>
          </p>
          {errorMessage ? (
            <p
              className="feature-client-detail__delete-error"
              role="alert"
            >
              {errorMessage}
            </p>
          ) : null}
        </div>
        <footer className="feature-client-detail__modal-foot">
          <button
            type="button"
            className="feature-client-detail__action-btn feature-client-detail__action-btn--ghost"
            onClick={onCancel}
            disabled={deleting}
          >
            Скасувати
          </button>
          <button
            type="button"
            className="feature-client-detail__action-btn feature-client-detail__action-btn--danger"
            onClick={onConfirm}
            disabled={deleting}
            data-loading={deleting ? "true" : undefined}
          >
            {deleting ? (
              <Spinner size="sm" label="Видалення" />
            ) : (
              <>
                <i className="fa-solid fa-trash" aria-hidden="true" />
                <span>Видалити</span>
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ClientDeleteConfirmModal;
